import {
  browserProfilingIntegration,
  browserTracingIntegration,
  extraErrorDataIntegration,
  init,
  replayIntegration,
  captureRouterTransitionStart,
} from '@sentry/nextjs';

import { AppConfig } from '@config';
import { POPUP_WINDOW_PREFIX } from '@lib/utils/openPopupWindow';

import type { THttpErrorMetadata } from '@lib/errors/HttpErrorResponse';

const isPopup =
  typeof window !== 'undefined' && (window.name?.startsWith(POPUP_WINDOW_PREFIX) ?? false);

const isProdAppEnv = AppConfig.appEnv === 'production';

if (isProdAppEnv && !isPopup) {
  init({
    dsn: AppConfig.sentryIngestionEndpoint,
    environment: AppConfig.appEnv,
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /chrome:\/\//i,
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
    ],
    integrations: [
      browserProfilingIntegration(),
      browserTracingIntegration({ enableInp: true }),
      extraErrorDataIntegration({ captureErrorCause: true }),
      replayIntegration({
        beforeErrorSampling: (event) => {
          const error = event?.contexts?.Error?.errorMetadata as Maybe<THttpErrorMetadata>;
          if (
            error != null &&
            (error?.statusCode === 400 || error?.statusCode === 403 || error?.statusCode === 404)
          ) {
            return false;
          }
          return true;
        },
        maskAllText: false,
        maskAllInputs: false,
        networkCaptureBodies: true,
        networkDetailAllowUrls: [
          'localhost',
          window.location.origin,
          /^https:\/\/end-gateway-kitt-dev\.azurewebsites\.com\/api/,
          /^https:\/\/end-gateway-kitt-qa\.azurewebsites\.com\/api/,
          /^https:\/\/end-gateway-kitt-prod\.azurewebsites\.com\/api/,
          /^https:\/\/end-gateway-kitt-prod-staging\.azurewebsites\.com\/api/,
        ],
        networkRequestHeaders: [
          'baggage',
          'sentry-trace',
          'x-contact-id',
          'x-contact-master-id',
          'x-contact-phone-number',
          'x-impersonation-azureadid',
          'x-correlation-id',
        ],
        stickySession: true,
      }),
    ],
    sampleRate: 0.1, // errors / exceptions
    tracesSampleRate: 0.05, // transactions / performance
    tracePropagationTargets: [
      'localhost',
      window.location.origin,
      /^https:\/\/end-gateway-kitt-dev\.azurewebsites\.com\/api/,
      /^https:\/\/end-gateway-kitt-qa\.azurewebsites\.com\/api/,
      /^https:\/\/end-gateway-kitt-prod\.azurewebsites\.com\/api/,
      /^https:\/\/end-gateway-kitt-prod-staging\.azurewebsites\.com\/api/,
    ],
    // % of Replays captured for non-errors
    replaysSessionSampleRate: 0,
    // % of Replays captured for errors.
    // Will slowly increase depending on number of errors/recording @ full
    replaysOnErrorSampleRate: 1,
  });
}

// This export will instrument router navigations, and is only relevant if you enable tracing.
// `captureRouterTransitionStart` is available from SDK version 9.12.0 onwards
export const onRouterTransitionStart = isPopup ? () => {} : captureRouterTransitionStart;
